import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, of, withLatestFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '@/environments/environment';
import { User } from '@/model/user';
import { KeycloakService } from 'keycloak-angular';
import { Store } from '@ngrx/store';
import * as UserActions from './user.actions';
import * as UserSelectors from './user.selectors';

@Injectable()
export class UserEffects {
  private apiRootUrl = `${environment.api}`;
  private cacheKey = 'sondis_user_cache';
  private cacheExpiration = 5 * 60 * 1000; // 5 minutes

  loadUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.loadUser),
      withLatestFrom(this.store.select(UserSelectors.selectUser)),
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      switchMap(([_, currentUser]) => {
        // Check if user is authenticated
        if (!this.keycloakService.isLoggedIn()) {
          return of(
            UserActions.loadUserFailure({ error: 'Not authenticated' }),
          );
        }

        // If we already have user data in store, start background refresh immediately
        if (currentUser !== null) {
          // Make API call in background to refresh
          setTimeout(() => {
            this.http
              .get<User>(`${this.apiRootUrl}/users/me`)
              .pipe(
                map((user) => {
                  // Update cache with fresh data
                  this.cacheUser(user);
                  return UserActions.loadUserSuccess({ user });
                }),
                catchError(() => of()), // Ignore background refresh errors
              )
              .subscribe((action) => {
                if (action) {
                  this.store.dispatch(action);
                }
              });
          }, 100);

          // Return success with current user (so loading state completes immediately)
          return of(UserActions.loadUserSuccess({ user: currentUser }));
        }

        // No user in store, check localStorage cache
        const cachedData = this.getUserFromCache();

        if (cachedData) {
          // Stale-while-revalidate: return cached data and start background refresh
          const backgroundRefresh = this.http
            .get<User>(`${this.apiRootUrl}/users/me`)
            .pipe(
              map((user) => {
                // Update cache with fresh data
                this.cacheUser(user);
                return UserActions.loadUserSuccess({ user });
              }),
              catchError(() => of()), // Ignore background refresh errors
            );

          // Start background refresh but don't wait for it
          setTimeout(() => {
            backgroundRefresh.subscribe((action) => {
              if (action) {
                this.store.dispatch(action);
              }
            });
          }, 100);

          // Return cached data immediately
          return of(UserActions.loadUserSuccess({ user: cachedData }));
        }

        // No cache and no store data, make API call normally
        console.log(
          '⚡ UserEffects: Making fresh API call (no cache, no store data)',
        );
        return this.http.get<User>(`${this.apiRootUrl}/users/me`).pipe(
          map((user) => {
            console.log('⚡ UserEffects: Fresh API response received:', user);
            // Cache the user data
            this.cacheUser(user);
            return UserActions.loadUserSuccess({ user });
          }),
          catchError((error) => {
            console.error('⚡ UserEffects: API call failed:', error);
            return of(UserActions.loadUserFailure({ error }));
          }),
        );
      }),
    ),
  );

  constructor(
    private actions$: Actions,
    private http: HttpClient,
    private keycloakService: KeycloakService,
    private store: Store,
  ) {}

  private getUserFromCache(): User | null {
    try {
      const cached = localStorage.getItem(this.cacheKey);
      if (!cached) return null;

      const { user, timestamp } = JSON.parse(cached);

      // Check if cache is still valid
      if (Date.now() - timestamp < this.cacheExpiration) {
        return user;
      }

      // Cache expired, remove it
      localStorage.removeItem(this.cacheKey);
      return null;
    } catch {
      // Invalid cache data, remove it
      localStorage.removeItem(this.cacheKey);
      return null;
    }
  }

  private cacheUser(user: User): void {
    try {
      const cacheData = {
        user,
        timestamp: Date.now(),
      };
      localStorage.setItem(this.cacheKey, JSON.stringify(cacheData));
    } catch {
      // localStorage might be full or unavailable, ignore error
    }
  }

  // Public method to clear cache (for logout)
  public clearUserCache(): void {
    localStorage.removeItem(this.cacheKey);
  }
}
